import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  SxProps,
  Typography,
} from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { GridColDef } from '@mui/x-data-grid'
import { CSSProperties, useEffect, useMemo, useState } from 'react'

import ModalActionContainer from 'components/containers/modalActionContainer'
import KanseiDataGrid from 'components/copied/dataGrid'
import { helper } from 'helper'
import { shiftQuestionnaireApi } from 'hooks/api/shiftQuestionnaire'
import { ApiSecurityFilterWithDetail } from 'types/security'
import { colors } from 'variables'

interface Props {
  securities: ApiSecurityFilterWithDetail[]
  onCancel: () => void
  onSend: (data: { year: number; month: number; securityIds: string[] }) => void
}

const apiCall = {
  getUnsubmitted: async () => {
    const response = await shiftQuestionnaireApi().filterByUnsubmitted()
    return response.data.unsubmitted
  },
}

type Row = {
  id: string
  name: string
  phone: string
  email: string
  securityField: string
}

const columns: GridColDef<Row>[] = [
  {
    field: 'name',
    headerName: '隊員名',
    width: 140,
    renderCell: (params) => (
      <Typography
        style={{
          lineHeight: '40px',
        }}
      >
        {params.row.name}
      </Typography>
    ),
  },
  { field: 'phone', headerName: '電話番号', width: 110 },
  { field: 'email', headerName: 'メールアドレス', flex: 1 },
  { field: 'securityField', headerName: '警備先', flex: 1 },
]

type UnsubmittedData = {
  year: number
  month: number
  securityIds: string[]
}

export default function NonAnsweredListModal(props: Props) {
  const [selectedMonth, setSelectedMonth] = useState<UnsubmittedData | null>(
    null,
  )
  const [questionnaireDates, setQuestionnaireDates] = useState<
    UnsubmittedData[]
  >([])

  // key: securityId
  const securityMap = useMemo(() => {
    return new Map<string, ApiSecurityFilterWithDetail>(
      props.securities.map((security) => [security.id, security]),
    )
  }, [props.securities])

  const rows = useMemo(() => {
    if (selectedMonth == null) return []

    const data: Row[] = []
    selectedMonth.securityIds.forEach((securityId) => {
      const security = securityMap.get(securityId)
      if (security == null) return
      data.push({
        id: security.id,
        name: helper.formatter.nameFormat(security),
        phone: security.cls2Detail.phoneNumber1, // 携帯電話
        email: security.cls2Detail.email,
        securityField: security.securityFields.map((v) => v.name).join('、'),
      })
    })
    return data
  }, [securityMap, selectedMonth])

  const isSelected = (date: { year: number; month: number }) => {
    if (selectedMonth == null) return false
    return (
      selectedMonth.year === date.year && selectedMonth.month === date.month
    )
  }

  // データの初期化
  useEffect(() => {
    apiCall.getUnsubmitted().then((questions) => {
      setQuestionnaireDates(questions)
    })
  }, [])

  // 初期選択
  useEffect(() => {
    if (questionnaireDates.length <= 0) return

    // 直近のデータを初期表示にする
    const first = questionnaireDates[0]
    setSelectedMonth(first)
  }, [questionnaireDates])

  const handleSelectMonth = (data: UnsubmittedData) => {
    setSelectedMonth(data)
  }

  const handleSend = () => {
    if (selectedMonth == null) return
    props.onSend(selectedMonth)
  }

  return (
    <>
      <Box style={containerStyle}>
        <Box style={monthListContainerStyle}>
          <Typography style={monthTitleStyle}>提出期間</Typography>
          <List disablePadding>
            {questionnaireDates.map((date) => (
              <ListItem disablePadding key={`${date.year}-${date.month}`}>
                <ListItemButton
                  onClick={() => handleSelectMonth(date)}
                  selected={isSelected(date)}
                >
                  <ListItemText
                    primary={`${date.year}年${date.month}月分`}
                    sx={{
                      ...monthTextStyle,
                      ...(isSelected(date) && {
                        fontWeight: 'bold',
                        color: colors.PRIMARY_COLOR,
                        textDecoration: 'none',
                      }),
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
        <Box style={securityListContainerStyle}>
          {selectedMonth && (
            <KanseiDataGrid
              rows={rows as []}
              columns={columns}
              handleChange={() => {}}
              checkboxSelection={false}
              isRowSelectable={() => false}
              sx={dataGridStyle}
            />
          )}
        </Box>
      </Box>
      <ModalActionContainer>
        <Button variant="negative" onClick={props.onCancel}>
          キャンセル
        </Button>
        <Button variant="contained" onClick={handleSend}>
          未提出者に再提出指示
        </Button>
      </ModalActionContainer>
    </>
  )
}

const containerStyle: CSSProperties = {
  display: 'flex',
  overflow: 'hidden',
  height: '500px',
  maxHeight: '80vh',
}

const monthTitleStyle: CSSProperties = {
  padding: '8px 16px',
}

const monthTextStyle: CSSProperties = {
  textDecoration: 'underline',
}

const monthListContainerStyle: CSSProperties = {
  width: '180px',
  backgroundColor: '#F7F8F8',
  paddingTop: '8px',
  overflowY: 'scroll',
}

const securityListContainerStyle: CSSProperties = {
  flex: 1,
  overflow: 'hidden',
}

const dataGridStyle: SxProps = {
  padding: '0 8px',
  '& .MuiDataGrid-virtualScroller': {
    overflowY: 'scroll',
  },
}
